import { TenantSchema, TenantSchemaProperty } from '@/utils/types/schemas';

const hasSchema = (
  json: TenantSchema['json'] | {}
): json is TenantSchemaProperty | Record<string, any> => {
  return (
    typeof json !== "string" &&
    json !== null &&
    json !== undefined &&
    typeof json === "object" &&
    "schema" in json
  );
};

const hasXComponent = (
  property: any
): property is { 'x-component': string; title: string; name: string } => {
  return (
    property &&
    typeof property === 'object' &&
    'x-component' in property &&
    typeof property['x-component'] === 'string' &&
    'title' in property &&
    typeof property.title === 'string'
  );
};

/**
 * Mirrors {@link ModuleFormPage}: replace CustomUpload / CustomImageUpload widget values with file ids before POST.
 */
export function normalizeDynamicModuleUploadFields(
  schemaJson: unknown,
  modifiedValues: Record<string, unknown>
): Record<string, unknown> {
  let out = { ...modifiedValues };

  if (!schemaJson || !hasSchema(schemaJson as TenantSchema['json'])) {
    return out;
  }

  const props = (schemaJson as TenantSchemaProperty).schema?.properties ?? {};
  const valueKeys = Object.keys(out);

  Object.entries(props).forEach(([fieldKey, property]) => {
    if (!hasXComponent(property)) return;

    // Use the schema property key (field name) to look up in formValues.
    // Fallback to property.title for legacy compatibility.
    const lookupKey = valueKeys.includes(fieldKey)
      ? fieldKey
      : valueKeys.includes(property.title)
      ? property.title
      : null;

    if (!lookupKey) return;

    if (property['x-component'] === 'CustomUpload') {
      const value = out[lookupKey] as unknown;
      if (Array.isArray(value) && value.length > 0 && value[0] && typeof value[0] === 'object' && 'id' in value[0]) {
        out = { ...out, [lookupKey]: (value[0] as { id: unknown }).id };
      }
    } else if (property['x-component'] === 'CustomImageUpload') {
      const value = out[lookupKey] as Record<string, unknown> | undefined;
      if (value && typeof value === 'object' && 'id' in value) {
        out = { ...out, [lookupKey]: value.id };
      }
    }
  });

  return out;
}
